import React, { useEffect, useState } from 'react'
import { ApolloClient, InMemoryCache, ApolloProvider, DefaultOptions } from '@apollo/client'
import Keycloak from 'keycloak-js'
import { KeycloakProvider, useKeycloak } from 'react-keycloak'
import { BrowserRouter as Router, Route, Switch, useHistory } from 'react-router-dom'
import { ThemeProvider, BaseTheme, AppBar, BarItems, Icons, SideBar, SideBarItem } from '@fabricks/ui-components'
// @ts-ignore
import './App.css'
// @ts-ignore
import './DarkApp.css'
import { SnackbarProvider, useSnackbar } from 'notistack'
import styled from 'styled-components'
import { get as _get } from 'lodash'
import Navigation from './constants/navigation'
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute'
import getEnvVars from './environment/environment'

import { isAdmin, isDeveloper } from './services/developerAccessService'
import { AppProgressIndicator } from './containers/AppProgressIndicator/AppProgressIndicator'

const defaultOptions: DefaultOptions = {
    watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
    },
    query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
    },
}

const client = new ApolloClient({
    uri: getEnvVars.bff.url,
    cache: new InMemoryCache(),
    defaultOptions,
})

const keycloak = Keycloak({
    url: getEnvVars.oidc.url,
    realm: getEnvVars.oidc.realm,
    clientId: getEnvVars.oidc.clientId,
})

// Hook
function useWindowSize() {
    const isClient = typeof window === 'object'

    function getSize() {
        return {
            width: isClient ? window.innerWidth : undefined,
            height: isClient ? window.innerHeight : undefined,
        }
    }

    const [windowSize, setWindowSize] = useState(getSize)

    useEffect(() => {
        if (!isClient) {
            return undefined
        }
        function handleResize() {
            setWindowSize(getSize())
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [getSize, isClient]) // Empty array ensures that effect is only run on mount and unmount

    return windowSize
}

function showDeveloperItems(assignedRoles: any, history: any) {
    if (Object.keys(assignedRoles).length !== 0 && assignedRoles.constructor === Object) {
        if (isDeveloper(assignedRoles)) {
            return (
                <SideBarItem
                    title="Developer"
                    onClick={() => history.push('/developer')}
                    icon={<Icons.DeveloperMode color="primary" />}
                />
            )
        }
    }
    return (<></>)
}

function showAdminItems(assignedRoles: any, history: any) {
    if (Object.keys(assignedRoles).length !== 0 && assignedRoles.constructor === Object) {
        if (isAdmin(assignedRoles)) {
            return (
                <>
                    <SideBarItem
                        title="Settings"
                        onClick={() => history.push('/settings')}
                        icon={<Icons.Settings color="primary" />}
                    />
                </>
            )
        }
    }
    return (<></>)
}

const HeaderAndSideBar: React.FC = () => {
    const [sideBarOpen, setSideBarOpen] = useState(false)
    const [keycloak] = useKeycloak()
    const history = useHistory()
    if (keycloak) {
        const assignedRoles = _get(keycloak, 'tokenParsed.resource_access', {})
        return (
            <>
                <AppBar
                    title="Warehouse Optimizer"
                    sideBarOpen={sideBarOpen}
                    sideBar
                    barItems={[
                        <BarItems.account
                            name={_get(keycloak, 'idTokenParsed.name', 'Fabricks')}
                            department={_get(keycloak, 'idTokenParsed.email', 'portal@fabricks.de')}
                        />,
                    ]}
                />
                <SideBar
                    isOpen={sideBarOpen}
                    onClickSidebarExpand={() => setSideBarOpen(true)}
                    onClickSidebarShrink={() => setSideBarOpen(false)}
                >
                    <SideBarItem
                        title="Dashboard"
                        onClick={() => history.push('/')}
                        icon={<Icons.Dashboard color="primary" />}
                    />
                    <SideBarItem
                        title="Optimization"
                        onClick={() => history.push('/optimization')}
                        icon={<Icons.Tune color="primary" />}
                    />
                    <SideBarItem
                        title="Deep Insights"
                        onClick={() => history.push('/deepinsights')}
                        icon={<Icons.Pageview color="primary" />}
                    />
                    <SideBarItem
                        title="Rack View"
                        onClick={() => history.push('/rackview')}
                        icon={<Icons.ViewModule color="primary" />}
                    />
                    <SideBarItem
                        title="Master data"
                        onClick={() => history.push('/masterdata')}
                        icon={<Icons.Build color="primary" />}
                    />
                    {showAdminItems(assignedRoles, history)}
                    {showDeveloperItems(assignedRoles, history)}
                </SideBar>
            </>
        )
    }
    return null
}

// TODO: Adjust the 73px based on sidebar open state
const AppContent = styled.div`
    padding-top: 90px;
    width: calc(100% - 73px);
`

const App: React.FC = () => {
    const renderRoutes = () => {
        return Object.keys(Navigation).map(routeKey => {
            return <ProtectedRoute path={Navigation[routeKey].path} exact component={Navigation[routeKey].component} />
        })
    }

    const viewMode = localStorage.getItem('viewMode') === 'dark' ? 'darkMode' : ''
    if (viewMode) {
        document.body.classList.add('darkModeAppWide')
    }

    return (
        <SnackbarProvider>
            <KeycloakProvider keycloak={keycloak}>
                <ThemeProvider theme={BaseTheme}>
                    <ApolloProvider client={client}>
                        <Router>
                            <div className={`App ${viewMode}`}>
                                <HeaderAndSideBar />
                                <AppContent>
                                    <Switch>{renderRoutes()}</Switch>
                                </AppContent>
                                <AppProgressIndicator timeoutInterval={25000} />
                            </div>
                        </Router>
                    </ApolloProvider>
                </ThemeProvider>
            </KeycloakProvider>
        </SnackbarProvider>
    )
}

export default App
